import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import "../../components/styles/profilePinPage.scss";

/**
 * ProfilePinPage — MangoTV Create PIN Page
 * Figma: 24654:6079
 *
 * Layout:
 *   - Navbar (fixed top)
 *   - Centered content area (vertically and horizontally):
 *     - Title: "Create a new pin" (24px, 700 weight, white, centered)
 *     - Subtitle: "Restrict logging into this account using PIN access" (14px, 400, neutral_400, centered)
 *     - 4 PIN input boxes in a row (centered):
 *       - Each box: ~48x48px, neutral_800 bg, border neutral_700, rounded_lg
 *       - First 2 boxes have digits "1" and "5" filled (white text, 20px, centered)
 *       - Last 2 boxes empty
 *       - Gap between boxes ~12px
 *     - Two buttons below (centered, side by side, gap 12px):
 *       - "Create pin" — outlined white border, transparent bg, 14px, 500 weight, ~140px wide
 *       - "Cancel" — filled neutral_800 bg, white text, 14px, 500 weight, ~140px wide
 *   - Footer at bottom
 */

const ProfilePinPage = () => {
  const navigate = useNavigate();
  const [pinDigits, setPinDigits] = useState(["1", "5", "", ""]);
  const inputRefs = useRef([null, null, null, null]);

  const handlePinChange = (index, value) => {
    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;

    // Allow single digit per input
    const newPinDigits = [...pinDigits];
    newPinDigits[index] = value.slice(0, 1);
    setPinDigits(newPinDigits);

    // Auto-focus to next input if digit is entered
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current is empty
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCreatePin = () => {
    const pin = pinDigits.join("");
    if (pin.length === 4) {
      // TODO: API call to create PIN
    }
  };

  const handleCancel = () => {
    setPinDigits(["1", "5", "", ""]);
    navigate("/settings");
  };

  return (
    <div className="profile-pin-page">
      {/* Navbar */}
      <Navbar activeTab="Settings" />

      {/* Main Content */}
      <div className="profile-pin-page__container">
        <div className="profile-pin-page__content">
          {/* Title */}
          <h1 className="profile-pin-page__title">Create a new pin</h1>

          {/* Subtitle */}
          <p className="profile-pin-page__subtitle">
            Restrict logging into this account using PIN access
          </p>

          {/* PIN Input Boxes */}
          <div className="profile-pin-page__pin-inputs">
            {pinDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                className="profile-pin-page__pin-input"
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                placeholder=""
              />
            ))}
          </div>

          {/* Action Buttons */}
          <div className="profile-pin-page__actions">
            <button
              className="profile-pin-page__btn profile-pin-page__btn--outline"
              onClick={handleCreatePin}
            >
              Create pin
            </button>
            <button
              className="profile-pin-page__btn profile-pin-page__btn--filled"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProfilePinPage;
